import { ComponentType, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import {
	DynamicIcon as DynamicIconRaw,
	iconNames,
	type IconName,
} from 'lucide-react/dynamic';
import { cn } from '@/lib/utils';
import {
	MATCH_MAX_MULTIPLE_SELECTIONS,
	joinMultipleAnswer,
	splitMultipleAnswer,
} from '../matchFlow';

const DynamicIcon = DynamicIconRaw as ComponentType<{
	name: IconName;
	size?: number;
	className?: string;
}>;

const isIconName = (name: string | null | undefined): name is IconName =>
	Boolean(name) && (iconNames as readonly string[]).includes(name as string);

interface MatchQuestionStepProps {
	question: Forms.FormQuestion;
	value: string | null | undefined;
	onChange: (value: string) => void;
	/** Chamado ao avançar (single: automático após escolher; multiple: botão). */
	onNext: () => void;
	isLast: boolean;
}

const OPTION_ENTER = {
	hidden: { opacity: 0, y: 12 },
	show: (i: number) => ({
		opacity: 1,
		y: 0,
		transition: { delay: 0.05 + i * 0.045, duration: 0.28 },
	}),
};

/** Uma pergunta por tela, alternativas em cartões, avanço automático na escolha única. */
const MatchQuestionStep = ({
	question,
	value,
	onChange,
	onNext,
	isLast,
}: MatchQuestionStepProps) => {
	const options = question.options ?? [];
	const isMultiple = question.type === 'MULTIPLE_CHOICE';
	const isFreeText = question.type === 'FREE_TEXT';
	const selectedKeys = isMultiple ? splitMultipleAnswer(value) : [];
	const accent = question.color?.trim() || '#E11D74';
	const canContinue = isFreeText
		? Boolean(value && value.trim().length > 0)
		: isMultiple
		  ? selectedKeys.length > 0
		  : Boolean(value);

	const handleSingle = (key: string) => {
		onChange(key);
		window.setTimeout(onNext, 260);
	};

	const handleToggle = (key: string) => {
		const already = selectedKeys.includes(key);
		let next: string[];
		if (already) {
			next = selectedKeys.filter((k) => k !== key);
		} else {
			if (selectedKeys.length >= MATCH_MAX_MULTIPLE_SELECTIONS) return;
			next = [...selectedKeys, key];
		}
		onChange(joinMultipleAnswer(question, next));
	};

	useEffect(() => {
		if (isFreeText) return;
		const onKeyDown = (event: KeyboardEvent) => {
			const target = event.target as HTMLElement | null;
			if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName))
				return;
			if (event.key === 'Enter' && canContinue) {
				event.preventDefault();
				onNext();
				return;
			}
			const index = Number.parseInt(event.key, 10);
			if (!Number.isNaN(index) && index >= 1 && index <= options.length) {
				const option = options[index - 1];
				if (!option) return;
				event.preventDefault();
				if (isMultiple) handleToggle(option.key);
				else handleSingle(option.key);
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [question.id, value, canContinue, isMultiple, isFreeText]);

	return (
		<div className="flex w-full flex-col gap-8">
			<div className="space-y-3">
				{isIconName(question.icon_identifier) ? (
					<span
						className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-white shadow-sm"
						style={{ backgroundColor: accent }}
					>
						<DynamicIcon
							name={question.icon_identifier}
							size={20}
						/>
					</span>
				) : null}
				<h1 className="text-2xl font-black leading-tight tracking-tight text-stone-900 md:text-[2rem]">
					{question.title}
				</h1>
				{question.description ? (
					<p className="text-sm font-medium text-stone-500 md:text-base">
						{question.description}
					</p>
				) : isMultiple ? (
					<p className="text-sm font-medium text-stone-500">
						Escolha até {MATCH_MAX_MULTIPLE_SELECTIONS} opções.
					</p>
				) : null}
			</div>

			{isFreeText ? (
				<textarea
					value={value ?? ''}
					onChange={(e) => onChange(e.target.value.slice(0, 2000))}
					rows={5}
					placeholder="Escreva com suas palavras..."
					className="w-full rounded-2xl border border-stone-200 bg-white px-5 py-4 text-base font-medium text-stone-800 outline-none transition-colors placeholder:text-stone-400 focus:border-stone-900"
				/>
			) : (
				<div
					className={cn(
						'grid gap-3',
						options.length > 4 ? 'sm:grid-cols-2' : 'grid-cols-1',
					)}
					role={isMultiple ? 'group' : 'radiogroup'}
				>
					{options.map((option, index) => {
						const selected = isMultiple
							? selectedKeys.includes(option.key)
							: value === option.key;
						const disabled =
							isMultiple &&
							!selected &&
							selectedKeys.length >=
								MATCH_MAX_MULTIPLE_SELECTIONS;
						return (
							<motion.button
								key={option.key}
								type="button"
								custom={index}
								variants={OPTION_ENTER}
								initial="hidden"
								animate="show"
								whileTap={{ scale: 0.985 }}
								disabled={disabled}
								onClick={() =>
									isMultiple
										? handleToggle(option.key)
										: handleSingle(option.key)
								}
								role={isMultiple ? 'checkbox' : 'radio'}
								aria-checked={selected}
								className={cn(
									'group flex w-full items-center gap-4 rounded-2xl border bg-white px-4 py-4 text-left transition-all',
									selected
										? 'border-stone-900 shadow-[0_6px_20px_-12px_rgba(0,0,0,0.35)] ring-1 ring-stone-900'
										: 'border-stone-200 hover:border-stone-400 hover:shadow-sm',
									disabled && 'cursor-not-allowed opacity-40',
								)}
							>
								<span
									className={cn(
										'flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border text-xs font-black transition-colors',
										selected
											? 'border-stone-900 bg-stone-900 text-white'
											: 'border-stone-200 bg-stone-50 text-stone-500 group-hover:border-stone-400',
									)}
								>
									{selected && isMultiple ? (
										<Check
											className="h-4 w-4"
											strokeWidth={3}
										/>
									) : (
										index + 1
									)}
								</span>
								<span className="flex-1 text-[15px] font-semibold leading-snug text-stone-800">
									{option.label}
								</span>
								{isIconName(option.icon_identifier) ? (
									<DynamicIcon
										name={option.icon_identifier}
										size={18}
										className="shrink-0 text-stone-400"
									/>
								) : null}
							</motion.button>
						);
					})}
				</div>
			)}

			{(isMultiple || isFreeText) && (
				<div className="flex items-center justify-between gap-4">
					<span className="text-xs font-medium text-stone-400">
						{isMultiple
							? `${selectedKeys.length} de ${MATCH_MAX_MULTIPLE_SELECTIONS} selecionadas`
							: null}
					</span>
					<button
						type="button"
						onClick={onNext}
						disabled={!canContinue}
						className={cn(
							'inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white transition-all',
							canContinue
								? 'bg-stone-900 hover:bg-stone-800'
								: 'cursor-not-allowed bg-stone-300',
						)}
					>
						{isLast ? 'Finalizar' : 'Continuar'}
						<ArrowRight className="h-4 w-4" />
					</button>
				</div>
			)}

			{!isMultiple && !isFreeText ? (
				<p className="text-xs font-medium text-stone-400">
					Dica: use as teclas 1 a {options.length} para responder mais
					rápido.
				</p>
			) : null}
		</div>
	);
};

export { MatchQuestionStep };
